import { FocusEvent, JSX, useCallback, useEffect, useRef, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { useDebouncedEffect } from 'core-components/hooks/useDebounced'
import { IInputCommonProps, IInputProps, Input } from 'core-components/library/inputs'
import { ISelectProps, Select, useFilterSource } from 'core-components/library/selects'
import { IValidatorProps, ValidateInput } from 'core-components/library/validation'
import { Logic, Operator } from 'core-components/types'

import { needHandleBlur, onBlurHelper } from './helpers/onBlurHelper'
import { useInputProps } from './hooks/useInputProps'
import { useOnInputKeyPress } from './hooks/useOnInputKeyPress'
import { usePopupOpenedState } from './hooks/usePopupOpenedState'
import { IDropdownCommon } from './models/IDropdownCommon'

export interface IDropdownDynamicProps<T = IForm> extends Omit<IDropdownCommon<T>, 'classes'> {
  /**
   * Classes for inner elements
   */
  classes?: {
    inputClasses?: IInputCommonProps['classes']
    controlClasses?: ISelectProps['classes']
    validatorClasses?: IValidatorProps['classes']
  }
  /**
   * Field from source to display
   */
  caption?: string
  /**
   * Fields to be searched
   */
  fieldsToSearch?: string[]
  /**
   * Customizable template for caption
   */
  calculateCaption?: (item: T) => string
  /**
   * Unused
   */
  validateName?: string // TODO: REMOVE
  /**
   * Component id attribute
   */
  id?: string // TODO: REMOVE
  /**
   * Change filtering method
   */
  mode?: string
  /**
   * asd
   */
  prepareInputConfig?: (config: IInputProps) => void
  /**
   * Custom layout for dropdown list items
   */
  renderFunction?: (item: T) => JSX.Element
}

export const DropdownDynamic = <T = IForm,>({
  source,
  fieldsToSearch,
  calculateCaption,
  onChange,
  label,
  placeholder,
  caption,
  value,
  checkFn,
  margin,
  disabled,
  mode,
  onBlur,
  autoFocus,
  prepareInputConfig,
  classes,
  dir,
  startContent,
  endContent,
  renderFunction,
  filter,
  itemClickCallback,
  _popupVisible,
  onDataLoad,
}: IDropdownDynamicProps<T>) => {
  const [inputted, setInputted] = useState<string>('')
  const [inputtedBeforeFocus, setInputtedBeforeFocus] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const { setPopupActions, onKeyPress } = useOnInputKeyPress()

  const { visibilityCallbackHandler, isPopupOpened } = usePopupOpenedState()

  const { staticSource, isDataLoading, updateSourceFilter } = useFilterSource<T>({
    source,
    caption: caption || calculateCaption || '',
    filter,
  })

  const itemRenderFunction = useCallback(
    (item: T) => {
      if (caption) {
        return item[caption]
      }
      if (calculateCaption) {
        return calculateCaption(item)
      }
      return undefined
    },
    [caption, calculateCaption]
  )

  const handleClick = useCallback(
    (item: T) => {
      // the key may not match the fieldToSearch
      if (mode === 'combobox' && fieldsToSearch && fieldsToSearch[0]) {
        onChange(item[fieldsToSearch[0]], item)
        setSearchValue('')
      } else {
        onChange(item[source.keyField], item)
        setSearchValue('')
      }

      if (onBlur) {
        onBlur()
      }
    },
    [mode, fieldsToSearch, onChange, onBlur]
  )

  const onChangeHandler = useCallback(
    (text: string) => {
      if ((inputted || searchValue) && !text) {
        onChange('')
      }
      setInputted(text)
      setSearchValue(text)
    },
    [inputted, searchValue, onChange]
  )

  const onBlurHandler = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      if (!needHandleBlur(event)) {
        inputRef.current?.focus()
        return
      }
      onBlurHelper(event, inputtedBeforeFocus, inputted, setInputted, onBlur)
      setSearchValue('')
    },
    [inputtedBeforeFocus, inputted, onBlur]
  )

  const inputRef = useRef<HTMLInputElement>(null)

  const inputProps = useInputProps({
    dir,
    startContent,
    endContent,
    classes: classes?.inputClasses,
    disabled,
    label,
    placeholder,
    autoFocus,
    inputted,
    inputRef,
    setInputtedBeforeFocus,
    isPopupOpened,
    onKeyPress,
    onChange: onChangeHandler,
    onBlur: onBlurHandler,
  })

  if (prepareInputConfig) {
    prepareInputConfig(inputProps)
  }

  useEffect(() => {
    if (value) {
      // When enter a symbol, such actions will greatly slow down the component. With combobox mode only change inputted
      if (mode !== 'combobox') {
        source.getById(value).then((el) => {
          el ? setInputted(itemRenderFunction(el)) : setInputted(`${value}`)
        })
      } else {
        setInputted(`${value}`)
      }
    } else {
      setInputted('')
    }
  }, [value, source])

  useDebouncedEffect(
    () => {
      if (searchValue) {
        const searchFilters = fieldsToSearch
          ? fieldsToSearch.map((field) => {
              return {
                field: field,
                operator: Operator.CONTAINS,
                value: searchValue,
              }
            })
          : [
              {
                field: caption || '',
                operator: Operator.CONTAINS,
                value: searchValue,
              },
            ]
        updateSourceFilter([
          {
            logic: Logic.OR,
            filters: searchFilters,
          },
        ])
      } else {
        updateSourceFilter([])
      }
      if (mode === 'combobox') {
        onChange(inputted, {})
      }
    },
    400,
    [searchValue, updateSourceFilter]
  )
  useEffect(() => {
    if (checkFn) {
      if (value) {
        source.getById(value).then((el) => {
          const newValue = el ? itemRenderFunction(el) : `${value}`
          setInputted(newValue)
        })
      } else {
        setInputted('')
      }
    }
  }, [checkFn])

  return (
    <Select<T>
      source={staticSource}
      wip={isDataLoading}
      classes={classes?.controlClasses}
      margin={margin}
      onClick={handleClick}
      disabled={disabled}
      itemClickCallback={itemClickCallback}
      initializePopupRef={setPopupActions}
      visibilityCallback={visibilityCallbackHandler}
      renderFunction={renderFunction || itemRenderFunction}
      onDataLoad={onDataLoad}
      _popupVisible={_popupVisible}
    >
      {checkFn ? (
        <ValidateInput
          {...inputProps}
          classes={{
            inputClasses: inputProps.classes,
            validatorClasses: classes?.validatorClasses,
          }}
          value={(inputProps.value as string) || ''}
          checkFn={checkFn}
        />
      ) : (
        <Input {...inputProps} />
      )}
    </Select>
  )
}
